import { useTranslation } from 'react-i18next';

import type { ContentBlockSpacing } from '@/components/molecules/ContentBlock';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { DomainFeedConversionRateDay } from '@/domain/biomass';
import type { DomainBiomassDay } from '@/domain/cages/overview.types';
import { OverviewChartCard, useOverviewChartSpecs } from '@/routes/Client/Cage/overviewCharts';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatDateTimeWorded } from '@/utils/date';

import styles from './CageWeightGraphsView.module.scss';

export interface CageWeightGraphsViewProps {
    days: DomainBiomassDay[];
    /** Dayparts that get a series, from `filterGraphDayparts`. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    /** The single daypart the charts that do not fan out should plot. */
    daypartTag: string;
    weightUnitTag: string;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    onDateChange: (dates: DateRange) => void;
    /** From a separate endpoint, so it carries its own loading and error state. */
    feedConversionRateDays: DomainFeedConversionRateDay[];
    isFeedConversionRateLoading: boolean;
    feedConversionRateError?: unknown;
    feedConversionRateRetry?: RetryProps;
    /** True while a live feeding refresh is running for this cage. */
    isFeedConversionRateRefreshing: boolean;
    /** True once the live-refresh poll budget is spent while still refreshing. */
    isFeedConversionRateExhausted: boolean;
    /** Newest fetch time for this cage's feeding rows; null until one has landed. */
    feedConversionRateLastFetchedAt: Date | null;
    /**
     * True when the other charts answer for a fish group (`fg`). The feeding service only knows
     * cages, so the FCR still covers the whole cage and its card says so.
     */
    isFeedConversionRateCageLevel?: boolean;
}

export default function CageWeightGraphsView(props: CageWeightGraphsViewProps) {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        isLoading,
        error,
        retry,
        dateFrom,
        dateTo,
        onDateChange,
        feedConversionRateDays,
        isFeedConversionRateLoading,
        feedConversionRateError,
        feedConversionRateRetry,
        isFeedConversionRateRefreshing,
        isFeedConversionRateExhausted,
        feedConversionRateLastFetchedAt,
        isFeedConversionRateCageLevel = false,
    } = props;
    const { t } = useTranslation('cageCharts');

    const specs = useOverviewChartSpecs({
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        feedConversionRateDays,
    });

    const sharedOverviewChartCardProps = {
        isLoading,
        error,
        ...retry,
        showDateRangePicker: false,
        contentBlockSpacing: 'none' as ContentBlockSpacing,
    };

    return (
        <>
            <div className={styles.datePickerSlot}>
                <div className={styles.datePickerRow}>
                    <DateRangePicker
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateChange={onDateChange}
                        defaultPresetId="none"
                        menuPosition="left"
                        disabled={isLoading}
                    />
                    {isFeedConversionRateRefreshing ? (
                        <p className={styles.refreshingHint}>
                            {isFeedConversionRateExhausted
                                ? feedConversionRateLastFetchedAt
                                    ? t('refresh.hintExhausted', {
                                          time: formatDateTimeWorded(
                                              feedConversionRateLastFetchedAt
                                          ),
                                      })
                                    : t('refresh.hintExhaustedNoTime')
                                : t('refresh.hint')}
                        </p>
                    ) : null}
                </div>
            </div>
            <GridLayout contentLayout="3">
                <OverviewChartCard
                    title={t('title.livingWeight')}
                    chartName="cage-living-weight"
                    spec={specs.livingWeight}
                    {...sharedOverviewChartCardProps}
                />
                <OverviewChartCard
                    title={t('title.measurements')}
                    chartName="cage-measurements"
                    spec={specs.measurements}
                    {...sharedOverviewChartCardProps}
                />
                <OverviewChartCard
                    title={t('title.growth')}
                    chartName="cage-growth"
                    spec={specs.growth}
                    {...sharedOverviewChartCardProps}
                />
                <OverviewChartCard
                    title={t('title.size')}
                    chartName="cage-size"
                    spec={specs.size}
                    {...sharedOverviewChartCardProps}
                />
                <OverviewChartCard
                    title={t('title.coefficientOfVariation')}
                    chartName="cage-cv"
                    spec={specs.coefficientOfVariation}
                    {...sharedOverviewChartCardProps}
                />
                <OverviewChartCard
                    title={t('title.conditionFactor')}
                    chartName="cage-condition-factor"
                    spec={specs.conditionFactor}
                    {...sharedOverviewChartCardProps}
                />
                <OverviewChartCard
                    title={t('title.feedConversionRatio')}
                    subtitle={
                        isFeedConversionRateCageLevel
                            ? t('subtitle.feedConversionRatioCageLevel')
                            : undefined
                    }
                    chartName="cage-feed-conversion-ratio"
                    spec={specs.feedConversionRatio}
                    isLoading={isFeedConversionRateLoading}
                    error={feedConversionRateError}
                    {...feedConversionRateRetry}
                    showDateRangePicker={false}
                    contentBlockSpacing="none"
                />
            </GridLayout>
        </>
    );
}
